const sanitizeLinkText = (text: string): string =>
  text.replace(/[[\]()]/g, '')

// Matches fenced code blocks (```...```) and inline code (`...`)
const CODE_BLOCK_RE = /```[\s\S]*?```|`[^`\n]+`/g
// \uFFFD (Unicode replacement char) never appears in valid UTF-8 text —
// safe to use as a placeholder boundary that cannot collide with real content.
const CODE_PLACEHOLDER = '\uFFFD'
const CODE_PLACEHOLDER_RE = new RegExp(`\\${CODE_PLACEHOLDER}CODE(\\d+)\\${CODE_PLACEHOLDER}`, 'g')

/**
 * Convert **_Place::Context_** markers to place-search: links.
 * Only converts markers that contain '::' — the documented format from the model prompt.
 * Plain **_text_** without '::' is valid bold+italic and is left unchanged.
 *
 * NOTE: This function does NOT protect code blocks. Callers that need code block
 * protection must extract them before calling and restore them after (see preprocessChatMarkdown).
 */
export function convertLegacyPlaceMarkers(text: string): string {
  return text.replace(/\*\*_(.+?)_\*\*/g, (match, marker) => {
    const separatorIndex = marker.indexOf('::')
    // TODO: simple markers without '::' (e.g. **_Paris_**) are left as bold+italic.
    // If future data shows old PG messages commonly had simple markers without context,
    // restore conversion here — it's safe because Trip Planner never used **_..._** for emphasis.
    // See PR #33693 for the trade-off discussion.
    if (separatorIndex === -1) return match
    const displayName = marker.substring(0, separatorIndex)
    const context = marker.substring(separatorIndex + 2)
    const safeDisplay = sanitizeLinkText(displayName)
    if (!safeDisplay) return match
    const searchQuery = `${displayName}, ${context}`
    return `[${safeDisplay}](place-search:${searchQuery.replace(/\)/g, '%29')})`
  })
}

const PLACE_SEARCH_PROTOCOL = 'place-search:'

/**
 * URL-encode place-search: link queries so marked can parse them as valid URLs.
 * GPT emits plain text queries: [Paris](place-search:Paris, France)
 * This encodes to: [Paris](place-search:Paris%2C%20France)
 *
 * Uses a character scanner with paren-depth counting instead of a regex, so it
 * correctly handles both:
 *  - Raw ) from BE-streamed queries: [Cafe Main](place-search:Cafe (Main), City)
 *  - Pre-encoded %29 from legacy markers: [Display](place-search:Query%29rest)
 * Both %28/%29 and literal (/) are counted for depth so the link boundary is
 * found regardless of how parens were encoded.
 */
export function encodePlaceSearchLinks(text: string): string {
  let result = ''
  let pos = 0

  while (pos < text.length) {
    const bracketStart = text.indexOf('[', pos)
    if (bracketStart === -1) { result += text.slice(pos); break }

    const bracketEnd = text.indexOf(']', bracketStart + 1)
    if (bracketEnd === -1) { result += text.slice(pos); break }

    const prefix = `(${PLACE_SEARCH_PROTOCOL}`
    if (text.slice(bracketEnd + 1, bracketEnd + 1 + prefix.length) !== prefix) {
      result += text.slice(pos, bracketStart + 1)
      pos = bracketStart + 1
      continue
    }

    const display = text.slice(bracketStart + 1, bracketEnd)
    const queryStart = bracketEnd + 1 + prefix.length

    // Find the link-closing ) using paren-depth counting.
    // Treat literal ( / ) AND %28 / %29 the same for depth so pre-encoded
    // and raw forms are handled identically.
    let depth = 1
    let i = queryStart
    while (i < text.length && depth > 0) {
      if (text[i] === '(') {
        depth++; i++
      } else if (text[i] === ')') {
        depth--; if (depth > 0) i++
      } else if (text.slice(i, i + 3) === '%28') {
        depth++; i += 3
      } else if (text.slice(i, i + 3) === '%29') {
        depth--; if (depth > 0) i += 3
      } else {
        i++
      }
    }

    if (depth !== 0) {
      result += text.slice(pos, bracketStart + 1)
      pos = bracketStart + 1
      continue
    }

    const query = text.slice(queryStart, i)
    result += text.slice(pos, bracketStart)
    let decoded: string
    try {
      decoded = decodeURIComponent(query)
    } catch {
      decoded = query
    }
    result += `[${display}](${PLACE_SEARCH_PROTOCOL}${encodeURIComponent(decoded)})`
    pos = i + 1
  }

  return result
}

/**
 * Preprocess chat message text before markdown rendering.
 * Converts legacy markers and encodes place-search queries.
 * Scoped to Trip Planner chat — NOT used by global renderMarkdown.
 */
export function preprocessChatMarkdown(text: string): string {
  // Never crash the page over a preprocessing failure — return original text
  // so the message still renders (just without clickable place links).
  try {
    // Extract code blocks for the whole pipeline so neither stage mutates them
    const codeBlocks: Array<string> = []
    const safeText = text.replace(CODE_BLOCK_RE, (match) => {
      codeBlocks.push(match)
      return `${CODE_PLACEHOLDER}CODE${codeBlocks.length - 1}${CODE_PLACEHOLDER}`
    })

    const processed = encodePlaceSearchLinks(convertLegacyPlaceMarkers(safeText))

    return processed.replace(CODE_PLACEHOLDER_RE, (_, i) => codeBlocks[Number(i)])
  } catch {
    return text
  }
}
